<template>
  <dynamic-object-form-structure
    :object-uuid="objectUUID"
    :object-layer="objectLayer"
    :loading-structure="loadingStructure"
    :loading-save="loadingSave"
    :permission-name="form.permission"
    :object-type-translation-key="form.name_translation"
    :tabs="visibleTabs"
    :active-tab="activeTab"
    :action-type="actionType"
    :map-element="$parent.$refs.formsContainer"
    :previous-pos-x="previousPosX"
    :previous-pos-y="previousPosY"
    :data-was-changed="dataWasChanged"
    :form-height-update="formSizeUpdate"
    :saveIsDisabled="objectIsFloorbox ? false : !this.required.filled"
    :fields-still-need-filled="objectIsFloorbox ? '' : required.stillNeedFilled"
    :min-width="765"
    @updateActiveTab="activeTab = $event"
    @changeViewMode="changeViewModeOfObject"
    @openDetails="openDetails"
    @refresh="refresh"
    @clearData="clearData"
    @save="saveObject"
    @close="closeForm"
    @resize-form="formSizeUpdate++"
    :object-data="objectData"
  >
    <template v-slot:formFields>
      <div
        v-show="visibleTabs[activeTab].type === 'photos'"
        class="form tab-content"
      >
        <forms-photo-section
          :disabled="readOnly"
          :imagesList="imagesList"
          :previous-images="previousImages"
          @updatePhoto="updatePhoto($event)"
          @removePhoto="addToPhotosToRemove($event)"
        />
      </div>

      <closures-form-splitters-tab
        v-show="visibleTabs[activeTab].code === 'closure_splitters'"
        :network-level="objectData.cl_network_level"
        object-endpoint="closures"
        :object-uuid="objectData.cl_uuid"
        :object-id="objectData.cl_id"
        :disabled="
          readOnly ||
          actionType === 'create' ||
          !userCanEditTabContent(visibleTabs[activeTab])
        "
        :action-type="actionType"
      />

      <closures-form-splicing-tab
        v-show="visibleTabs[activeTab].code === 'closure_splicing'"
        object-endpoint="closures"
        :object-uuid="objectData.cl_uuid"
        :object-id="objectData.cl_id"
        :network-level="objectData.cl_network_level"
        :visible="visibleTabs[activeTab].code === 'closure_splicing'"
        :form-size-update="formSizeUpdate"
        :disabled="
          readOnly || actionType === 'create' || visibleTabs[activeTab].disable
        "
        :action-type="actionType"
        @update-connections="updatedSplicingConnections = $event"
        @set-initial-connections="initialConnections = $event"
        @update-form-height="formSizeUpdate++"
      />

      <accessories-and-others-tab
        v-show="visibleTabs[activeTab].label === 'Accessories'"
        :accessories-selected="accessoriesSelected"
        :accessories-selected-by-material-id="accessoriesSelectedByMaterialId"
        relation-family-id-select="cl"
        family-id="ac"
        :disabled="readOnly"
      />
      <accessories-and-others-tab
        v-show="visibleTabs[activeTab].label === 'Others'"
        :accessories-selected="othersSelected"
        :accessories-selected-by-material-id="othersSelectedByMaterialId"
        relation-family-id-select="cl"
        family-id="ot"
        :disabled="readOnly"
      />

      <div
        v-show="visibleTabs[activeTab].type === 'form'"
        class="form tab-content"
      >
        <template v-for="field in visibleTabs[activeTab].fields">
          <input-field
            v-if="field.var_name === 'cl_material_id'"
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="getValueInObject(field.var_name, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            inputType="other"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :hide="field.hide"
            :fieldDisabled="
              field.disable || objectData.cl_network_level === null
            "
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
          >
            <treeselect
              :value="getValueInObject(field.var_name, field.input_type)"
              @input="
                setValueInObject(field.var_name, $event, field.input_type)
              "
              :options="materialOptions"
              :load-options="loadMaterialOptions"
              :normalizer="materialOptionsNormalizer"
              :disabled="
                readOnly ||
                !userCanEditTabContent(visibleTabs[activeTab]) ||
                field.disable ||
                objectData.cl_network_level === null
              "
              class="form-input-select"
              disableFuzzyMatching
            />
          </input-field>

          <input-field
            v-else
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="getValueInObject(field.var_name, field.input_type)"
            @input="setValueInObject(field.var_name, $event, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            :inputType="field.input_type"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :selectOptions="field.select_list_name"
            :radioOptions="field.radio_options"
            :fieldDisabled="field.disable"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
            :hide="field.hide"
            :automatic-label="field.has_automatic_label"
            @generateLabel="getAutomaticLabel(autoLabelRequestData)"
            :checkboxOptions="field.checkbox_options"
            :object-data="objectData"
            :labelIsLocked="objectData.cl_label_locked"
            :user-can-unlock-label="!userHasFibercloudPermission(form.permission + '_manage_label')"
            @toggle-label-lock="changeLabelMode"
            @updateCheckboxValue="updateCheckboxValue($event)"
            @removePhoto="addToPhotosToRemove($event)"
          />
        </template>
      </div>

      <custom-fields-tab
        v-if="visibleTabs[activeTab].code === 'custom_fields'"
        :customFields="customFields.custom_fields"
        :objectData="objectData.custom_fields"
        :actionType="actionType"
        :disabled="readOnly"
        @updateValue="setValueInObject"
        :requiredFilled="required.filled"
      />
    </template>

    <template v-slot:footerButtons>
      <v-btn
        @click="openClosureToExport"
        color="accent"
        class="footer-button"
        :disabled="!hasExportPermissions"
      >
        {{ $t("SplicePlan") }}
      </v-btn>
    </template>
  </dynamic-object-form-structure>
</template>

<script>
import { bus } from "@/main.js";
import { dynamicObjectFormsMixin } from "@/mixins/forms/dynamicObjectForms/dynamicObjectFormsMixin";
import DynamicObjectFormStructure from "../_Templates/DynamicObjectFormStructure";
import FormsPhotoSection from "../_SpecialTabs/Photos/PhotosForm";
import InputField from "../_Molecules/InputField";
import ClosuresFormSplittersTab from "@/components/Home/Forms/_SpecialTabs/Splitters/SplittersTab";
import ClosuresFormSplicingTab from "@/components/Home/Forms/_SpecialTabs/Splicing/SplicingTab";
import AccessoriesAndOthersTab from "@/components/Home/Forms/_SpecialTabs/AccessoriesAndOthers/AccessoriesAndOthersTab";
import CustomFieldsTab from "@/components/Home/Forms/_SpecialTabs/CustomFields/CustomFieldsTab.vue";
import { mapActions } from "vuex";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "ClosureForm",

  components: {
    AccessoriesAndOthersTab,
    ClosuresFormSplittersTab,
    ClosuresFormSplicingTab,
    InputField,
    FormsPhotoSection,
    DynamicObjectFormStructure,
    CustomFieldsTab,
  },

  mixins: [dynamicObjectFormsMixin,checkPermissionsMixin],

  props: {
    technicalPointData: {
      type: Object,
    },
  },

  data() {
    return {
      updatedSplicingConnections: null,
      formSizeUpdate: 0,
    };
  },
  mounted(){
    bus.$emit("reset-closure-without-TP")
  },
  computed: {
    objectIsFloorbox(){
      if(this.objectData.hasOwnProperty('cl_logical_type') && this.objectData.cl_logical_type === 'FB'){
        return true
      }
      return false
    },
    autoLabelRequestData() {
      let textGeom, projection;

      if (this.actionType === "create") {
        projection = 3857;
        textGeom = this.wkt;
      } else {
        projection = 4326;
        textGeom = this.dataReceived.technicalpoint.node.nd_text_geom;
      }

      return {
        layer: this.objectLayer,
        text_geom: textGeom,
        projection: projection,
      };
    },

    hasTechnicalPoint() {
      return (
        this.technicalPointData &&
        Object.keys(this.technicalPointData).length > 0
      );
    },

    hasPermissionToSeeSplitters() {
      return this.userHasFibercloudPermission(
        "logical_object_splitter",
        "show"
      );
    },

    hasExportPermissions(){
      const create_export_permission = this.userHasPermission("export", "management","store");
      return create_export_permission;
    },

    technicalPointType() {
      return this.$store.getters.getClosureWithoutTechPoint;
    },

    materialRequestQueryParameters() {
      let queryParameters = {
        workspace_uuid: this.$store.getters.workspace.uuid,
        material_family_id: "cl",
      };
      if (this.objectData.cl_logical_type) {
        queryParameters.closure_type = this.objectData.cl_logical_type;
      }
      return queryParameters;
    },
  },

  watch: {
    loadingStructure(newValue) {
      if (!newValue && this.actionType === "create") {
        this.objectData.technicalpoint = this.technicalPointData;
      }
      if (this.actionType === "splicing") {
        let splicingTabIndex = this.visibleTabs.findIndex((tab) =>
          tab.code.includes("splicing")
        );
        if (splicingTabIndex > -1) {
          this.activeTab = splicingTabIndex;
        }
      }
      if (!newValue) {
        this.objectData.coordinate_x = this.xCoordinate;
        this.objectData.coordinate_y = this.yCoordinate;
        this.getPreviousImages();
      }
    },

    "objectData.cl_logical_type"() {
      let varName = "cl_material_id";
      if (
        this.materialOptions &&
        !this.materialOptions.find(
          (material) => material.id === this.objectData.cl_material_id
        )
      ) {
        this.updateDependentFieldValueInObject(varName, false);
      }
      this.materialOptions = null;
    },

    "objectData.cl_material_id"(newID) {
      this.updateMaterialDependentFields(newID);
    },

    materialOptions() {
      if (this.materialOptions && this.objectData.cl_material_id) {
        this.updateMaterialDependentFields(this.objectData.cl_material_id);
      }
    },
  },

  methods: {
    ...mapActions(["downloadSplicePlan"]),

    tabIsEnabled(tab) {
      return !(
        (tab.label === "Technical Point" &&
          this.actionType === "create" &&
          !this.hasTechnicalPoint) ||
        (tab.code === "closure_splitters" && this.actionType === "create") ||
        (tab.code === "closure_splicing" && this.actionType === "create")
      );
    },

    createObject() {
      this.createClosureWithTechPoint();
    },

    async createClosureWithTechPoint() {
      this.createDataToSend();

      try {
        await this.CREATE_OBJECTS({
          endpoint: this.form.endpoint,
          data: [this.dataToSend],
        });

        this.objectData.cl_label = this.incrementLabelSequential(
          this.objectData.cl_label
        );

        this.finishObjectCreation();
      } catch (e) {
        if (e.response.data.data) {
          this.$toasted.global.toast_error(e.response.data.data);
        } else {
          this.$toasted.global.toast_error(e.response.data.message);
        }
      } finally {
        this.$emit("clear-next-form");
        this.loadingSave = false;
      }
    },

    /**
     * Gest the list of exports
     */
    async openClosureToExport() {
      this.loading = true;
      try {
        let data = {
          type: "excel",
          version: "splice_plan_v1",
          objects: {
            workspace_uuid: this.materialRequestQueryParameters.workspace_uuid,
            object: [
              {
                uuid: this.objectData.cl_uuid,
                object_type: "closure"
              }
            ]
          }
        }

        this.exports = await this.downloadSplicePlan(data);
        if (this.exports.file_url_public) {
          window.open(this.exports.file_url_public)
        } else {
          this.$toasted.global.toast_error(this.$i18n.t("Fail"));
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    createDataToSend() {
      this.dataToSend = {
        technical_point: {
          text_geom: this.hasTechnicalPoint
            ? this.wkt
            : this.$attrs.techPointGeom,
          projection: 3857,
          uuid: this.hasTechnicalPoint && this.technicalPointData
            ? this.technicalPointData.tp_uuid
            : this.$attrs.closureTechPointData.tp_uuid,
        },
        status: this.objectData.cl_status,
        progress: this.objectData.cl_progress,
      };
      if (this.technicalPointData && this.technicalPointData.split_cable) {
        this.dataToSend.split_cable = this.technicalPointData.split_cable;
      }
      if (this.accessoriesSelected || this.othersSelected) {
        this.dataToSend.accessories = [];
        this.generateAccessoriesDataSend(this.accessoriesSelected);
        this.generateAccessoriesDataSend(this.othersSelected);
      }
      this.placeObjectDataInDataToSend();
    },
  },
};
</script>

<style scoped>
.footer-button {
  font-size: 8px !important;
  height: 22px !important;
  margin: 1px 0 0 10px;
}

</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/ObjectForms/ClosureForm.vue